import { useEffect, useState } from 'react';
import { setElementProp, rgb, type Color, type ProjectDoc } from '@cal/engine';
import { get, post, put, uploadFile, errorMessage } from '../api';
import { ColorField, Field } from '../ui/controls';
import { toast } from '../ui/toast';
import { assetStore, thumbUrl } from '../assets';
import { edit, useEditor } from './store';

/** Logo, contact details, brand colours and brand presets (Content → Brand). */
interface BrandPreset { id: string; name: string; customer_id: string | null; data: { logoAssetId: string | null; contact: string; colors: Color[]; headingFontId: string | null; bodyFontId: string | null } & Partial<Record<'company' | 'tagline' | 'phone' | 'email' | 'website' | 'address' | 'personName' | 'personTitle', string>> }

export function BrandSection({ doc }: { doc: ProjectDoc }) {
  const project = useEditor((s) => s.project);
  const [presets, setPresets] = useState<BrandPreset[]>([]);
  const [uploading, setUploading] = useState(false);
  const load = () => get<{ brands: BrandPreset[] }>(`/api/brands${project?.customerId ? `?customerId=${project.customerId}` : ''}`).then((r) => setPresets(r.brands)).catch(() => undefined);
  useEffect(() => { void load(); }, [project?.customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  const b = doc.brand;
  const setBrand = (patch: Partial<ProjectDoc['brand']>, key = 'brand') => edit((d) => ({ ...d, brand: { ...d.brand, ...patch } }), key);
  const onLogo = async (f: File) => {
    setUploading(true);
    try {
      const r = await uploadFile<{ asset: Parameters<typeof assetStore.add>[0] }>('/api/assets?kind=logo', f);
      assetStore.add(r.asset);
      setBrand({ logoAssetId: r.asset.id });
      toast(r.asset.hasAlpha ? 'Logo uploaded (transparent background kept).' : 'Logo uploaded.', 'ok');
    } catch (e) { toast(errorMessage(e), 'err'); } finally { setUploading(false); }
  };
  const autoContact = [b.company, b.phone, b.email, b.website].map((x) => x.trim()).filter(Boolean).join(' · ');
  const headingFont = (doc.designs.month.elements.find((e) => e.id === 'heading') as { style?: { fontId: string } } | undefined)?.style?.fontId ?? null;
  return (
    <div className="section col">
      <h3>Brand & contact details</h3>
      <Field label="Customer / title name" hint="Used by {CUSTOMER} (cover and backing board)"><input className="input" value={b.customerName} onChange={(e) => setBrand({ customerName: e.target.value }, 'brand-name')} /></Field>

      <div className="col">
        <span className="small muted">Contact details. Each detail shows wherever the design uses it; empty ones are left out.</span>
        <div className="col">
          <div className="grid2">
            {([['company', 'Company', '{COMPANY}'], ['tagline', 'Tagline', '{TAGLINE}'], ['phone', 'Phone', '{PHONE}'], ['email', 'Email', '{EMAIL}'], ['website', 'Website', '{WEBSITE}'], ['address', 'Address', '{ADDRESS}'], ['personName', 'Person / agent name', '{PERSON_NAME}'], ['personTitle', 'Job title', '{PERSON_TITLE}']] as const).map(([k, label, token]) => (
              <Field key={k} label={label} hint={token}>
                <input className="input" value={b[k]} onChange={(e) => setBrand({ [k]: e.target.value } as Partial<ProjectDoc['brand']>, `brand-${k}`)} />
              </Field>
            ))}
          </div>
        </div>
        <Field label="Contact line" hint="Used by {CONTACT}, a one-line footer on some templates">
          <textarea className="input" rows={2} value={b.contact} onChange={(e) => setBrand({ contact: e.target.value }, 'brand-contact')} />
        </Field>
        {autoContact && autoContact !== b.contact && (
          <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => setBrand({ contact: autoContact }, 'brand-contact')} title={autoContact}>Fill the contact line from the details above</button>
        )}
      </div>
      <div className="row">
        {b.logoAssetId ? <div style={{ width: 90, height: 48, background: `repeating-conic-gradient(#eee 0 25%, #fff 0 50%) 0 0/12px 12px` }}><img alt="Logo" src={thumbUrl(b.logoAssetId)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></div> : <span className="small faint">No logo</span>}
        <label className="btn sm">{uploading ? 'Uploading…' : b.logoAssetId ? 'Replace logo' : 'Upload logo'}<input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => e.target.files?.[0] && onLogo(e.target.files[0])} /></label>
        {b.logoAssetId && <button className="btn sm ghost danger" onClick={() => setBrand({ logoAssetId: null })}>Remove</button>}
      </div>
      <Field label="Brand colours">
        <div className="row wrap">
          {b.colors.map((c, i) => (
            <div key={i} style={{ width: 110 }}><ColorField label={`Colour ${i + 1}`} value={c} onChange={(nc) => setBrand({ colors: nc ? b.colors.map((x, j) => (j === i ? nc : x)) : b.colors.filter((_, j) => j !== i) })} allowNone /></div>
          ))}
          {b.colors.length < 6 && <button className="btn sm" onClick={() => setBrand({ colors: [...b.colors, rgb('#d9481c')] })}>+ Add</button>}
        </div>
      </Field>
      <div className="row wrap">
        {presets.length > 0 && (
          <select className="input" style={{ flex: 1 }} aria-label="Apply brand preset" value="" onChange={(e) => {
            const p = presets.find((x) => x.id === e.target.value);
            if (!p) return;
            edit((d) => {
              const extra = Object.fromEntries((['company', 'tagline', 'phone', 'email', 'website', 'address', 'personName', 'personTitle'] as const).filter((k) => typeof p.data[k] === 'string').map((k) => [k, p.data[k]]));
              let out: ProjectDoc = { ...d, brand: { ...d.brand, ...extra, presetId: p.id, logoAssetId: p.data.logoAssetId, contact: p.data.contact, colors: p.data.colors?.length ? p.data.colors : d.brand.colors } };
              if (p.data.headingFontId) for (const design of ['month', 'cover', 'backing', 'year', 'face', 'carrier'] as const) for (const el of out.designs[design].elements) if (el.type === 'text' && el.role === 'heading') out = setElementProp(out, 'all', design, el.id, ['style', 'fontId'], p.data.headingFontId);
              return out;
            }, 'brand-preset');
            toast(`Applied brand preset “${p.name}”`, 'ok');
          }}>
            <option value="">Apply a saved brand preset…</option>
            {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <button className="btn sm" onClick={async () => {
          const name = prompt('Name for this brand preset', b.customerName || project?.customerName || 'Brand');
          if (!name) return;
          const data = { logoAssetId: b.logoAssetId, contact: b.contact, colors: b.colors, headingFontId: headingFont, bodyFontId: null, company: b.company, tagline: b.tagline, phone: b.phone, email: b.email, website: b.website, address: b.address, personName: b.personName, personTitle: b.personTitle };
          try {
            const existing = presets.find((p) => p.id === b.presetId);
            if (existing && confirm(`Update the existing preset “${existing.name}”? (Cancel creates a new one.)`)) await put(`/api/brands/${existing.id}`, { name, customerId: project?.customerId ?? null, data });
            else await post('/api/brands', { name, customerId: project?.customerId ?? null, data });
            toast('Brand preset saved for this customer', 'ok');
            void load();
          } catch (e) { toast(errorMessage(e), 'err'); }
        }}>Save as brand preset</button>
      </div>
    </div>
  );
}

